import { LAYER_ID_TENANT_SEPARATOR, LayerKey } from '@festina-life/api'
import { typeUtils } from '@festina-life/core'
import { useFormikContext, useTranslate } from '@festina-life/ui/hooks'
import { PathSelector } from '@festina-life/ui/listsAndTables'
import { Icon } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useTranslateLayer } from '../../hooks/useTranslateLayer'
import { useConfigurationItemsTree } from '../../scenes/serverConfigurationItems/useConfigurationItemsTree'
import { useSortedLayersOnBlock } from '../../uiModels/api/metaModel/useSortedLayersOnBlock'
import { initialFilterStatus } from '../configurationItemsTree/types'

const validateKey = typeUtils.createValidator<{ packagePath: string[] }>()

type Props = {
  layer: LayerKey
  block: string
}

export const DraftPathSelector = ({ layer, block }: Props) => {
  const translate = useTranslate()
  const translateLayer = useTranslateLayer()
  const configurationItemsTree = useConfigurationItemsTree({
    shouldShowEmptyNodes: false,
    shouldIncludeLayers: false,
    filterStatus: {
      ...initialFilterStatus,
      layerFilter: layer,
      blockFilter: {
        mode: 'withBaseBlocks',
        selectedBlock: block,
      },
    },
  })
  const { values, setFieldValue, errors } = useFormikContext<{
    packagePath: string[]
  }>()
  const allLayers = useSortedLayersOnBlock(block)
  const layerNamesToStringKeys = useMemo(
    () =>
      new Map(
        allLayers?.map((layerKey) => [
          translateLayer(layerKey),
          layerKey.toString(),
        ]),
      ),
    [allLayers, translateLayer],
  )

  return (
    <PathSelector
      title={translate('configurationTree.pathSelector.title')}
      trees={configurationItemsTree}
      value={values.packagePath}
      onChange={(nextPath) => {
        const pathWithLayersAsKeys: string[] = []
        let isParsingLayers = true
        for (const part of nextPath) {
          if (isParsingLayers) {
            const layerKey = layerNamesToStringKeys.get(part)
            if (layerKey) {
              pathWithLayersAsKeys.push(layerKey)
              continue
            }
            isParsingLayers = false
          }
          pathWithLayersAsKeys.push(part)
        }

        void setFieldValue(validateKey('packagePath'), pathWithLayersAsKeys)
      }}
      error={
        Array.isArray(errors.packagePath)
          ? errors.packagePath?.join(',')
          : errors.packagePath
      }
      renderNode={(node) => {
        const isLayer =
          node.path.at(-1)?.includes(LAYER_ID_TENANT_SEPARATOR) ?? false
        return (
          <>
            <Icon
              name={isLayer ? 'Square3Stack3D' : 'Folder'}
              size="md"
              color="unset"
            />
            {node.name}
          </>
        )
      }}
      nameFromPathPart={(pathPart) => {
        if (allLayers?.map((layer) => layer.toString()).includes(pathPart)) {
          const layerKey = new LayerKey(pathPart)
          return translateLayer(layerKey)
        }
        return pathPart
      }}
    />
  )
}
